import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 60000,
});

export const tokenStore = {
  get access() {
    return localStorage.getItem("access_token");
  },
  get refresh() {
    return localStorage.getItem("refresh_token");
  },
  set(access, refresh) {
    if (access) localStorage.setItem("access_token", access);
    if (refresh) localStorage.setItem("refresh_token", refresh);
  },
  clear() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  },
};

// Attach Bearer token (works inside preview iframe where 3rd-party cookies are blocked)
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined" && window.__TOOL_TOKEN) {
    config.headers["X-Tool-Token"] = window.__TOOL_TOKEN;
    return config;
  }
  const t = tokenStore.access;
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Auto-refresh access token on 401, then retry once
let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthCall =
      url.includes("/auth/login") || url.includes("/auth/logout") || url.includes("/auth/refresh");
    const isToolLink = typeof window !== "undefined" && !!window.__TOOL_TOKEN;
    if (status === 401 && original && !original._retry && !isAuthCall && !isToolLink) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = api
            .post("/auth/refresh", { refresh_token: tokenStore.refresh })
            .finally(() => {
              refreshing = null;
            });
        }
        const { data } = await refreshing;
        if (data?.access_token) tokenStore.set(data.access_token, null);
        return api(original);
      } catch (e) {
        // Log out ONLY when refresh is explicitly rejected (invalid/expired session).
        // Transient origin errors (520/5xx/network) must NOT kick the user out.
        if (e.response?.status === 401) {
          tokenStore.clear();
          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            window.location.assign("/login");
          }
        }
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const eur = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

export const num = (v) => new Intl.NumberFormat("it-IT").format(v || 0);

// URL del logo partner utilizzabile in <img> (token via query, perché <img> non invia il Bearer header)
export const partnerLogoUrl = (id) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/partners/${id}/logo?t=${encodeURIComponent(tokenStore.access || "")}`;

export const maybeFunnelPrompt = (res) => {
  const fp = res?.data?.funnel_prompt;
  if (fp) window.dispatchEvent(new CustomEvent("prospect-funnel-prompt", { detail: fp }));
  return res;
};

export default api;
